import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter, OnChanges } from '@angular/core';
import * as urlTemplate from 'url-template';
import { map, tap } from 'rxjs/operators';

import { AutocompletePlace } from '@shared/base-search-bar/entities/autocomplete';
import { IMAGE_TYPE } from '@ux/components/image/constants/image-type.constants';
import { SearchType } from '@search-bar/entities/search-type';
import { PlaceNamePipe } from '@app/pipes/place-name.pipe';
import { PlaceType } from '@shared/base-search-bar/entities/place-type';
import { searchProductTypeMapper } from '@app/modules/core/mappers/amplitude-mappers';
import { getProductPopularDestinations } from '@shared/base-search-bar/mappers/popular-destinations.mapper';
import { AssetPopularDestinations } from '@asset-popular-destinations';
import { EventProperties } from '@avo/interface/trigger-props';
import {
  TypographyFontSize, TypographyColor, TypographyFontFamily
} from '@design-system/components/atoms/typography/entities/typography.types';
import { ImageSize } from '@design-system/components/molecules/image/entities/image.types';

@Component({
  selector: 'app-search-bar-popular-destinations',
  templateUrl: './search-bar-popular-destinations.component.html',
  styleUrls: ['./search-bar-popular-destinations.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SearchBarPopularDestinationsComponent implements OnChanges {

  public destinations: string[] = [];
  public typeImage: string = IMAGE_TYPE.FLIGHT;

  @Input() arrivalPlace: string;
  @Input() departurePlace: string;
  @Input() placeType: PlaceType;
  @Input() searchType: SearchType;
  @Input() codeSelected: SearchType;
  @Input() active: boolean;

  @Output() selectPlace: EventEmitter<AutocompletePlace> = new EventEmitter<AutocompletePlace>();
  @Output() popularSelected: EventEmitter<EventProperties> = new EventEmitter<EventProperties>();

  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;
  public readonly fontFamily = TypographyFontFamily;

  public readonly imageSize = ImageSize.None;
  public readonly imageBorderRadius = 'var(--border-radius-nano)';

  constructor(
    private placeNamePipe: PlaceNamePipe
  ) { }

  ngOnChanges(): void {
    if (this.active) this.setDestinations();
  }

  get activePopularDestinations(): boolean {
    return !!this.destinations && this.destinations.length > 0;
  }

  get productType(): string {
    return searchProductTypeMapper(this.searchType);
  }

  get activeDestinationInputId(): string {
    return `active-search-place-input-${this.placeType}`;
  }

  public setDestinations(): void {
    this.destinations = getProductPopularDestinations(this.searchType, this.placeType, this.departurePlace);
  }

  public imageDestination(code: string): string {
    return urlTemplate.parse(AssetPopularDestinations.BASE_IMAGE).expand({ code: code.toLocaleLowerCase() });
  }

  public selectDestination(code: string): void {
    this.placeNamePipe.transform(code).pipe(
      map((placeName) => {
        const country = placeName ? placeName.split(',') : [];
        return ({
          country, props: {
            TRAVEL_PRODUCT_TYPE: this.productType,
            CITY: country.length > 0 ? country[0] : '',
            INDEX: this.destinations.findIndex(x => x === code),
            CONTROL_ID: this.activeDestinationInputId
          }
        });
      }),
      tap(({ country, props }) => {
        this.popularSelected.emit(props);
        this.selectPlace.emit({
          code: code,
          country: country.length > 1 ? country[1] : '',
          description: country.length > 0 ? country[0] : '',
          related_city: country.length > 0 ? country[0] : ''
        } as AutocompletePlace);
      })
    ).subscribe().unsubscribe();
  }
}
